import ErrorPage from '@components/common/ErrorPage';
import Loading from '@components/common/Loading';
import React, {Suspense, lazy} from 'react';
import SecureRoute from './SecureRoute';
import {Redirect, Route, Switch, useLocation} from 'react-router-dom';
import {Routes} from '@account/utils/route';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {useAuth} from '@apps-common/auth-react';

const AddAreasOfAccessPage = lazy(
  () => import('@components/provisioning/areas-of-access/AddAreasOfAccessPage')
);
const AddUsersPage = lazy(
  () => import('@components/provisioning/users/AddUsersPage')
);
const AllContractsPage = lazy(
  () => import('@components/provisioning/all-contracts/AllContractsPage')
);
const ContractDetailPage = lazy(
  () => import('@components/provisioning/contract-detail/ContractDetailPage')
);
const ContractValidationPage = lazy(
  () =>
    import(
      '@components/provisioning/contract-validation/ContractValidationPage'
    )
);
const ProvisioningOverviewPage = lazy(
  () =>
    import(
      '@components/provisioning/provisioning-overview/ProvisioningOverviewPage'
    )
);
const CreateCustomProvisionPage = lazy(
  () =>
    import(
      '@components/provisioning/custom-provision/CreateCustomProvisionPage'
    )
);
const LabsPage = lazy(() => import('@components/labs/LabsPage'));
const OrderPage = lazy(() => import('@account/components/orders/OrderPage'));
const MyOrdersPage = lazy(() => import('@components/orders/MyOrdersPage'));
const Organization = lazy(
  () => import('@components/organizations/Organization')
);
const OrgsPage = lazy(
  () => import('@account/components/organizations/OrgsPage')
);
const PVContractDetailPage = lazy(
  () => import('@components/plans/PVContractDetailPage')
);
const PVLineItemPage = lazy(
  () => import('@components/common/pv-contracts/PVLineItemPage')
);
const PlanDetailPage = lazy(
  () => import('@account/components/plans/PlanDetailPage')
);
const PlansPage = lazy(() => import('@account/components/plans/PlansPage'));
const SentinelHubRedirectPage = lazy(
  () => import('@account/components/app/sentinel-hub/SentinelHubRedirectPage')
);
const TaskingContractDetailPage = lazy(
  () => import('@components/tasking/TaskingContractDetailPage')
);
const UserDashboardPage = lazy(
  () => import('@components/users/UserDashboardPage')
);
const UserProfilePage = lazy(() => import('@components/users/UserProfilePage'));
const UsersTablePage = lazy(() => import('@components/users/UsersTablePage'));

const AppRouter = () => {
  const auth = useAuth();
  const {pathname} = useLocation();

  const isOrgUser = hasRoleLevel(auth, [roleLevels.org_user]);
  const isOrgAdminOrPlaneteer = hasRoleLevel(auth, [
    roleLevels.org_admin,
    roleLevels.planet_user,
    roleLevels.planet_admin,
  ]);

  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        {/* Remove trailing slashes */}
        <Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} />

        {/* Planetary Variables redirects, set in place July 2023 */}
        <Redirect
          from="/planetary-variable-contracts/add-contract"
          to={Routes.CONTRACTS_ADD}
        />
        <Redirect
          from="/planetary-variable-contracts/:contractId/products/:lineItemId"
          to={Routes.CONTRACT_LINEITEM_DETAIL}
        />
        <Redirect
          from="/planetary-variable-contracts/:contractId"
          to={Routes.CONTRACT_DETAIL}
        />
        <Redirect from="/planetary-variable-contracts" to={Routes.CONTRACTS} />
        <Redirect
          from="/plans/planetary-variable-contract/:contractId/product/:lineItemId"
          to={Routes.PLAN_CONTRACT_LINEITEM_DETAIL}
        />
        <Redirect
          from="/plans/planetary-variable-contract/:contractId"
          to={Routes.PLAN_CONTRACT_DETAIL}
        />

        {/* Contracts */}

        <SecureRoute
          path={Routes.CONTRACTS_ADD}
          component={CreateCustomProvisionPage}
          roles={[roleLevels.planet_admin]}
          exact
        />

        <SecureRoute
          path={Routes.CONTRACTS_ALL}
          component={AllContractsPage}
          roles={[roleLevels.planet_admin]}
          exact
        />

        <SecureRoute
          path={Routes.CONTRACT_LINEITEM_DETAIL}
          component={PVLineItemPage}
          roles={[roleLevels.planet_admin]}
          exact
        />

        <SecureRoute
          path={Routes.CONTRACT_ADD_AOAS}
          component={AddAreasOfAccessPage}
          roles={[roleLevels.planet_admin]}
          exact
        />

        <SecureRoute
          path={Routes.CONTRACT_VALIDATE}
          component={ContractValidationPage}
          roles={[roleLevels.planet_admin]}
          exact
        />

        <SecureRoute
          path={Routes.CONTRACT_ADD_USERS}
          exact
          component={AddUsersPage}
          roles={[roleLevels.planet_admin]}
        />

        <SecureRoute
          path={Routes.CONTRACT_DETAIL}
          component={ContractDetailPage}
          roles={[roleLevels.planet_admin]}
          exact
        />

        <SecureRoute
          path={Routes.CONTRACTS}
          component={ProvisioningOverviewPage}
          roles={[roleLevels.planet_admin]}
          exact
        />

        {/* Dashboard */}

        <SecureRoute
          path={Routes.DASHBOARD}
          exact
          component={UserDashboardPage}
          roles={[roleLevels.org_user]} // for now only org users get this view
        />

        {/* Labs */}

        <SecureRoute
          path={Routes.LABS}
          component={LabsPage}
          roles={[roleLevels.planet_user, roleLevels.planet_admin]}
        />

        {/* Orders */}

        <Route path={Routes.ORDER_DETAIL} exact component={OrderPage} />

        <Route path={Routes.ORDERS} exact component={MyOrdersPage} />

        {/* Organizations */}

        <SecureRoute
          path={Routes.ORG_DETAIL}
          component={Organization}
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        <SecureRoute
          path={Routes.ORGS}
          component={OrgsPage}
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        {/* Plans */}

        <Route path={Routes.PLAN_TASKING} exact>
          <Redirect to={`${Routes.PLANS}?tab=tasking`} />
        </Route>

        <Route path={Routes.PLAN_TASKING_DETAIL} exact>
          <TaskingContractDetailPage />
        </Route>

        <Route path={Routes.PLAN_CONTRACT_DETAIL} exact>
          <PVContractDetailPage />
        </Route>

        <Route path={Routes.PLAN_CONTRACT_LINEITEM_DETAIL} exact>
          <PVLineItemPage />
        </Route>

        <Route path={Routes.PLAN_CONTRACTS} exact>
          <Redirect to={`${Routes.PLANS}?tab=pv`} />
        </Route>

        <Route path={Routes.PLAN_DETAIL} exact>
          <PlanDetailPage />
        </Route>

        <Route path={Routes.PLANS} component={PlansPage} />

        {/* Users */}

        <SecureRoute
          path={Routes.USER_DETAIL}
          component={UserProfilePage}
          exact
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        <SecureRoute
          path={Routes.USERS}
          component={UsersTablePage}
          exact
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        <Route path={Routes.USER_SETTINGS} exact component={UserProfilePage} />

        <Route
          path={Routes.SENTINEL_HUB_REDIRECT}
          exact
          component={SentinelHubRedirectPage}
        />

        {/* Root */}

        <Route path={Routes.ROOT} exact>
          {isOrgUser && <Redirect to={Routes.DASHBOARD} />}

          {isOrgAdminOrPlaneteer && <Redirect to={Routes.ORGS} />}

          <ErrorPage />
        </Route>

        <Route path="*">
          <ErrorPage statusCode={404} />
        </Route>
      </Switch>
    </Suspense>
  );
};

export default AppRouter;
